import { Link } from "react-router-dom";
import { KIND_METADATA, type CollectionKind } from "@atelier/config";
import { ArtworkImage } from "@/components/ArtworkImage";
import { cn } from "@/lib/cn";

export type CollectionTileData = {
  id: string;
  chainId: number;
  contractAddress: `0x${string}`;
  name: string;
  symbol: string;
  imageCid?: string;
  kind: CollectionKind;
  pfpProjectId?: string | null;
};

export function CollectionTile({
  c,
  shortName,
  ipfsGateway,
  index,
}: {
  c: CollectionTileData;
  shortName: string;
  ipfsGateway: string;
  index: number;
}) {
  const img = c.imageCid ? `${ipfsGateway}${c.imageCid}` : null;
  // PFP-derived collections are technically Drop contracts but the
  // user-facing label should reflect the origin (procedural generation),
  // not the underlying ABI.
  const label = c.pfpProjectId ? "pfp" : KIND_METADATA[c.kind].pillLabel;
  const color = c.pfpProjectId
    ? "bg-cyan-500 text-cyan-50"
    : KIND_METADATA[c.kind].pillClasses;

  return (
    <Link
      to={`/${shortName}/${c.contractAddress}`}
      className={cn(
        "group relative flex flex-col overflow-hidden rounded-xl border border-border bg-card",
        "transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:border-foreground/40",
        "fade-rise",
      )}
      style={{ animationDelay: `${Math.min(index, 11) * 40}ms` }}
    >
      <div className="relative aspect-square w-full overflow-hidden bg-muted">
        {img ? (
          <ArtworkImage
            src={img}
            alt={c.name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-3xl font-semibold text-muted-foreground/50">
            {c.symbol.slice(0, 2) || "??"}
          </div>
        )}
        <span
          className={cn(
            "absolute right-2 top-2 rounded-md px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide",
            color,
          )}
        >
          {label}
        </span>
      </div>
      <div className="flex flex-col gap-0.5 p-3">
        <div className="truncate text-sm font-semibold">{c.name}</div>
        <div className="truncate text-xs text-muted-foreground">{c.symbol}</div>
      </div>
    </Link>
  );
}
